import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useFocusTrap from '../../hooks/useFocusTrap';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import { Overlay, ModalContainer, CloseModalButton } from './Modal.styles';
import ModalPortal from './ModalPortal';
import { Icon } from '@oneaudi/unified-web-components';

interface ModalProps {
  onClose: () => void;
  width?: string;
  isDealerNotesModal?: boolean;
  onKeyDown: (event: React.KeyboardEvent) => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  onClose,
  children,
  width,
  onKeyDown,
  isDealerNotesModal,
}) => {
  useLockBodyScroll();
  const { t } = useTranslation();

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target !== e.currentTarget) return;
      onClose();
    },
    [onClose],
  );

  const focusTrapRef = useFocusTrap(true, {
    focusElementOnOpen: "div[data-component='info-modal'] > button", // close modal button
  });

  return (
    <ModalPortal>
      <Overlay
        aria-label={t('featuresAndSpecsSection.ariaLabel.backdrop')}
        onClick={handleClick}
        tabIndex={0}
        onKeyDown={handleKeyPress}
        isDealerNotesModal={isDealerNotesModal}
      >
        <ModalContainer
          role="dialog"
          aria-label={t('featuresAndSpecsSection.ariaLabel.modal')}
          aria-modal="true"
          width={width}
          id="modalContainerVDPFSS"
          tabIndex={0}
          onKeyDown={onKeyDown}
          ref={focusTrapRef}
          data-component="info-modal"
          isDealerNotesModal={isDealerNotesModal}
        >
          <CloseModalButton
            onClick={onClose}
            aria-label={t('featuresAndSpecsSection.ariaLabel.closeModal')}
            id="closeModalVDPFSS"
            data-testid="close-modal"
          >
            <Icon name="cancel" size="s" />
          </CloseModalButton>
          {children}
        </ModalContainer>
      </Overlay>
    </ModalPortal>
  );
};

export default Modal;
